/**
 * Unsaved-changes navigation guard.
 *
 * The app uses a declarative <BrowserRouter>, where React Router's useBlocker is
 * unavailable (it requires a data router). So instead of blocking at the router,
 * we expose a small context:
 *
 *  - Entry forms call useRegisterUnsavedChanges(isDirty) to register their dirty
 *    state. The provider tracks whether ANY registered form is dirty.
 *  - The nav chrome (Sidebar / BottomNav) calls confirmDiscard() before following
 *    a link and preventDefaults the navigation if the owner cancels.
 *  - While anything is dirty, a beforeunload handler also covers hard navigation
 *    (tab close, reload, typing a new URL).
 *
 * This deliberately does NOT catch arbitrary in-page navigate() calls (e.g. a
 * form's own save→redirect), which is correct: those are intentional and the form
 * clears its dirty state as it unmounts.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useConfirm } from './ConfirmDialog';

interface UnsavedChangesContextValue {
  /** True while at least one registered form has unsaved edits. */
  isDirty: boolean;
  /** Register/clear a form's dirty state under a stable id. */
  setDirty: (id: string, dirty: boolean) => void;
  /** Resolves true if it's safe to navigate away (nothing dirty, or the owner
   *  confirmed discarding). Nav chrome awaits this before following a link.
   *
   *  Async because the prompt is now the app's own styled dialog rather than a
   *  native confirm(): callers must preventDefault first and navigate themselves
   *  once it resolves. Nothing is dirty in the overwhelming majority of clicks,
   *  in which case it resolves immediately and the navigation is indistinguishable
   *  from an unguarded one. */
  confirmDiscard: () => Promise<boolean>;
}

const UnsavedChangesContext = createContext<UnsavedChangesContextValue | null>(null);

export const UnsavedChangesProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // Registrant id → dirty. A ref for identity-stable writes; a derived boolean in
  // state so consumers re-render only when overall dirtiness flips.
  const dirtyMapRef = useRef<Map<string, boolean>>(new Map());
  const [isDirty, setIsDirty] = useState(false);
  const { confirm, dialog } = useConfirm();
  const { t } = useTranslation();

  const setDirty = useCallback((id: string, dirty: boolean) => {
    const map = dirtyMapRef.current;
    if (dirty) map.set(id, true);
    else map.delete(id);
    let any = false;
    for (const v of map.values()) {
      if (v) {
        any = true;
        break;
      }
    }
    setIsDirty((prev) => (prev === any ? prev : any));
  }, []);

  const confirmDiscard = useCallback(async () => {
    if (!isDirty) return true;
    return confirm({
      title: t('shared.unsavedChanges.title'),
      message: t('shared.unsavedChanges.message'),
      confirmLabel: t('shared.unsavedChanges.confirm'),
      cancelLabel: t('shared.unsavedChanges.cancel'),
    });
  }, [isDirty, confirm, t]);

  // Browser-level navigation (tab close / reload / URL change) while dirty.
  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Required by some browsers to trigger the native prompt.
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  const value = useMemo(
    () => ({ isDirty, setDirty, confirmDiscard }),
    [isDirty, setDirty, confirmDiscard],
  );

  return (
    <UnsavedChangesContext.Provider value={value}>
      {children}
      {dialog}
    </UnsavedChangesContext.Provider>
  );
};

/**
 * Access the guard. Outside a provider (e.g. an isolated component test) this
 * returns an inert no-op so consumers never crash and navigation is never blocked.
 */
export function useUnsavedChanges(): UnsavedChangesContextValue {
  const ctx = useContext(UnsavedChangesContext);
  if (!ctx) {
    return { isDirty: false, setDirty: () => {}, confirmDiscard: async () => true };
  }
  return ctx;
}

/**
 * Register this component's dirty state with the guard. Pass a boolean that is
 * true whenever the form holds unsaved edits. The registration clears
 * automatically on unmount, so a form that saves and navigates away never leaves
 * a stale "dirty" flag behind.
 */
export function useRegisterUnsavedChanges(dirty: boolean): void {
  const { setDirty } = useUnsavedChanges();
  const id = useId();
  useEffect(() => {
    setDirty(id, dirty);
    return () => setDirty(id, false);
  }, [dirty, id, setDirty]);
}
